import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import {
  useGetV1Plants,
  useGetV1SitesIdEffectiveConfig,
  type EnvelopeEffectiveConfigDataParametersItem as Param,
  type FigureState,
  type MetaQuietOneOf,
  type Reason,
} from "@solarkey/api-client";
import { ErrorState, Skeleton, StateWord } from "@solarkey/ui";

import { PageHeader } from "../shell/PageHeader";

/* ════════════════════════════════════════════════════════════════════════════
   M5 — Configuration packs (wireframe §07, Admin). The site's effective config
   resolved from tenant / region pack / site overrides: region pack, tariff and
   value basis, grid limits and thresholds, plus validation-on-load. Every value
   says which layer it came from.

   Every value on this screen now comes from GET /v1/sites/{id}/effective-config
   (directive §5.7, Envelope_EffectiveConfig): `parameters[].value` with its
   `unit`, and `source_layer` plus the effective window of the row that supplied
   it — which is exactly the wireframe's "From" column. The resolve date is the
   server's `resolved_at`, never the browser clock (CLAUDE.md rule 7).

   This screen used to render `config-pack.json`: an invented pack with an
   invented tariff, invented grid limits and three validation results that had
   passed nowhere. The layout, the controls and the row list are Shubham's and
   are kept; what changed is where the values come from. A fact the resolver
   does not hold keeps its row and prints a state word with the true reason
   instead of a plausible number (CLAUDE.md rule 6 — never a silent default).

   Currency, timezone and locale are DATA here: they arrive as resolved
   parameters, so no literal of either appears in this file (CLAUDE.md rule 3).
   ════════════════════════════════════════════════════════════════════════════ */

/** What to say when the resolver holds nothing for a wireframe row. */
interface Withheld {
  state: FigureState;
  /** An existing grammar template, when one fits; the prose below always shows. */
  template?: string;
  /** The true reason, shown in the "From" column in place of a layer. */
  reason: string;
}

interface RowSpec {
  label: string;
  /** Which resolved parameter keys supply this row. */
  match: (key: string) => boolean;
  withheld: Withheld;
}

interface CardSpec {
  key: string;
  title: string;
  rows: RowSpec[];
  /** Keys this card also owns, beyond the named rows above. */
  extra?: (key: string) => boolean;
  footer?: string;
}

/** A registry field nobody has authored — the closest template in the grammar. */
const NOT_AUTHORED = "NOT_MEASURED_REGISTRY_FIELD_NOT_AUTHORED";

const oneOf = (keys: string[]) => (key: string) => keys.includes(key);
const prefixed = (prefixes: string[]) => (key: string) =>
  prefixes.some((p) => key.startsWith(p));

const CARDS: CardSpec[] = [
  {
    key: "region",
    title: "Region pack",
    rows: [
      {
        label: "Locale and grouping",
        match: oneOf(["locale"]),
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no loaded layer carries a locale — neither the region pack nor the tenant row",
        },
      },
      {
        label: "Time zone",
        match: oneOf(["timezone"]),
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no time zone is recorded on the site row; it is a data field and is never assumed here",
        },
      },
      {
        label: "Financial year start",
        match: oneOf(["financial_year_start"]),
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no financial-year start is recorded on the tenant row",
        },
      },
      {
        label: "Emission factor",
        match: oneOf(["emission_factor_kg_per_kwh"]),
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "the region pack supplies this and no pack is loaded for this site; it never defaults (rule 6)",
        },
      },
      {
        label: "Regulatory terms",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no table holds regulatory terms and the resolver answers no such key",
        },
      },
      {
        label: "Reference PR basis",
        match: () => false,
        withheld: {
          state: "NOT_APPLICABLE",
          template: "NOT_APPLICABLE_FIXED_BY_RULING",
          reason: "fixed by the KPI ruling (DC basis, ratio of sums) — not a per-site resolvable value",
        },
      },
    ],
    extra: oneOf(["timezone_tenant_default"]),
  },
  {
    key: "tariff",
    title: "Tariff and value basis",
    footer:
      "Without a tariff every money figure reads Cannot check. Money is never computed inside an engine.",
    rows: [
      {
        label: "Value basis",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no value-basis column exists in any configuration table",
        },
      },
      {
        label: "Tariff",
        match: prefixed(["tariff_rate"]),
        withheld: {
          state: "NOT_MEASURED",
          template: "NO_TARIFF",
          reason: "no tariff band is in force for this site on the resolved date",
        },
      },
      {
        label: "Budget table",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no budget or plan table is loaded for this site",
        },
      },
      {
        label: "Currency",
        match: oneOf(["currency"]),
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no loaded layer carries a currency; it is a data field, never a literal here (rule 3)",
        },
      },
      {
        label: "Time-of-day bands",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "band structure is not resolved as a value; each band in force arrives as its own tariff row",
        },
      },
    ],
    extra: prefixed(["tariff_", "demand_charge"]),
  },
  {
    key: "grid",
    title: "Grid limits and thresholds",
    rows: [
      {
        label: "Export limit",
        match: prefixed(["grid_export"]),
        withheld: {
          state: "NOT_MEASURED",
          template: "NOT_MEASURED_NO_INTERCONNECTION_TERMS",
          reason: "no interconnection-terms table exists, so no layer holds an export limit",
        },
      },
      {
        label: "Transformer ratings",
        match: prefixed(["transformer_"]),
        withheld: {
          state: "NOT_MEASURED",
          template: NOT_AUTHORED,
          reason: "no transformer constants are registered against the devices at this site",
        },
      },
      {
        label: "Ramp rate",
        match: prefixed(["grid_ramp"]),
        withheld: {
          state: "NOT_MEASURED",
          template: "NOT_MEASURED_NO_INTERCONNECTION_TERMS",
          reason: "no interconnection-terms table exists, so no layer holds a ramp rate",
        },
      },
      {
        label: "Deep-curtailment threshold",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: "NOT_MEASURED_NO_TENANT_POLICY_TABLE",
          reason: "no tenant policy table is loaded, so no confirmation threshold is held",
        },
      },
      {
        label: "Coverage floor",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: "NOT_MEASURED_NO_THRESHOLD_TABLE",
          reason: "no threshold table is loaded; the floor lives in the detection rules, not in config",
        },
      },
      {
        label: "Offline gap to Not measured",
        match: () => false,
        withheld: {
          state: "NOT_MEASURED",
          template: "NOT_MEASURED_NO_THRESHOLD_TABLE",
          reason: "an open item in the ruling — no layer authors this gap yet",
        },
      },
    ],
    extra: prefixed(["grid_", "nominal_frequency", "under_frequency", "over_frequency"]),
  },
];

/* The wireframe's three validation-on-load checks. The frozen contract serves no
   validation result for any of them — Envelope_EffectiveConfig carries resolved
   parameters only — so the panel keeps its rows and withholds each outcome. */
const CHECKS: { label: string; withheld: Withheld }[] = [
  {
    label: "Schema valid",
    withheld: {
      state: "NOT_MEASURED",
      template: "NOT_MEASURED_NO_PACK_VALIDATION_RESULTS",
      reason: "no endpoint reports pack schema validation; effective-config returns resolved values only",
    },
  },
  {
    label: "No literals in code",
    withheld: {
      state: "NOT_MEASURED",
      template: "NOT_MEASURED_NO_PACK_VALIDATION_RESULTS",
      reason: "this is a CI gate outcome; no platform endpoint serves build-gate results",
    },
  },
  {
    label: "Second-country test",
    withheld: {
      state: "NOT_MEASURED",
      template: "NOT_MEASURED_NO_PACK_VALIDATION_RESULTS",
      reason: "no endpoint reports a pack-portability run for this build",
    },
  },
];

/** Human labels for the resolver's own keys (the extra rows a card picks up). */
const LABELS: Record<string, string> = {
  currency: "Currency",
  emission_factor_kg_per_kwh: "Emission factor",
  financial_year_start: "Financial year starts",
  locale: "Locale and grouping",
  nominal_frequency_hz: "Nominal frequency",
  over_frequency_hz: "Over-frequency limit",
  region_pack_code: "Region pack",
  timezone: "Time zone",
  timezone_tenant_default: "Time zone, tenant default",
  under_frequency_hz: "Under-frequency limit",
};

const sentence = (text: string): string =>
  text.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

function humanise(key: string): string {
  return LABELS[key] ?? sentence(key);
}

/** The value as the server stated it. The unit travels WITH the value (UX-003 §6.2). */
function formatValue(p: Param): string {
  const raw = p.value;
  let text: string;
  if (typeof raw === "number") {
    text = raw.toLocaleString(undefined, { maximumFractionDigits: 4 });
  } else if (typeof raw === "boolean") {
    text = raw ? "yes" : "no";
  } else if (typeof raw === "string") {
    text = raw;
  } else {
    text = JSON.stringify(raw);
  }
  return p.unit ? `${text} ${p.unit}` : text;
}

/** "site · from 2026-09-01 to 2027-03-31" — the layer that won, and its window. */
function formatFrom(p: Param): string {
  const parts = [p.source_layer ?? "unresolved"];
  if (p.effective_from) parts.push(`from ${p.effective_from}`);
  if (p.effective_to) parts.push(`to ${p.effective_to}`);
  return parts.join(" · ");
}

const reasonOf = (w: Withheld): Reason | undefined =>
  w.template ? { template: w.template } : undefined;

/* ── The screen ────────────────────────────────────────────────────────────── */

export function Configuration(
  { site, tenant, scope }: { site?: string; tenant?: string; scope?: string },
): ReactNode {
  // No hardcoded site: the scope comes from the URL (…/admin/configuration?site=ID),
  // and without one the screen says so and offers the server's own site list.
  return site ? <ResolvedConfiguration site={site} /> : <UnscopedConfiguration tenant={tenant} scope={scope} />;
}

function ResolvedConfiguration({ site }: { site: string }): ReactNode {
  const query = useGetV1SitesIdEffectiveConfig(site, undefined);

  if (query.isLoading) {
    return (
      <div className="flex flex-col" style={{ gap: 14 }} aria-busy="true">
        <Skeleton variant="text" width="16rem" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="tile" />)}
        </div>
        <Skeleton variant="row" lines={4} />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Configuration unavailable"
        message="The resolved configuration for this site could not be read. Nothing is shown rather than a stale or assumed value."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const envelope = query.data.data;
  const params = (envelope.data?.parameters ?? []).filter((p): p is Param & { key: string } =>
    typeof p.key === "string" && p.key !== "",
  );

  return (
    <ConfigurationBody
      site={site}
      params={params}
      resolvedAt={envelope.data?.resolved_at ?? undefined}
      ruleVersion={envelope.meta?.rule_version ?? undefined}
      quiet={envelope.meta?.quiet ?? undefined}
    />
  );
}

/* No site in the URL. The screen keeps its shape and withholds every value with
   the true reason, and the picker lists the sites the server returns — never a
   default site, which would state another plant's configuration under this one. */
function UnscopedConfiguration({ tenant, scope }: { tenant?: string; scope?: string }): ReactNode {
  const navigate = useNavigate();
  const plants = useGetV1Plants({});
  const items = plants.data?.status === 200 ? plants.data.data.data?.items ?? [] : [];
  const siteIds = items.map((i) => i.site_id).filter((id): id is string => !!id);
  const select = (id: string) =>
    void navigate({ search: ((prev: Record<string, unknown>) => ({ ...prev, site: id })) as never });

  return (
    <ConfigurationBody
      site={undefined}
      params={[]}
      noSite
      picker={
        <div className="card">
          <h4>
            Choose a site
            <span className="sub">
              configuration resolves per site (directive §5.7) — no site is assumed
            </span>
          </h4>
          {plants.isLoading ? (
            <Skeleton variant="row" lines={2} />
          ) : siteIds.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              {siteIds.map((id) => (
                <button key={id} type="button" className="btn" onClick={() => select(id)}>
                  {id}
                </button>
              ))}
            </div>
          ) : (
            <p style={noteStyle}>
              The site list could not be read, so no site can be offered here. Open this screen
              with a site in the URL (…/admin/configuration?site=SITE_ID).
              {tenant && scope ? ` Current scope: ${tenant} · ${scope}.` : null}
            </p>
          )}
        </div>
      }
    />
  );
}

/* ── The wireframe body: header, three cards, validation on load ───────────── */

function ConfigurationBody(props: {
  site?: string;
  params: (Param & { key: string })[];
  resolvedAt?: string;
  ruleVersion?: string;
  quiet?: MetaQuietOneOf | null;
  noSite?: boolean;
  picker?: ReactNode;
}): ReactNode {
  const { params, noSite } = props;
  const [packTestOpen, setPackTestOpen] = useState(false);

  // Without a site nothing could be resolved; the state word says exactly that.
  const unresolvable: Withheld | undefined = noSite
    ? {
        state: "CANNOT_CHECK",
        template: "CANNOT_CHECK_NO_SITE_ID",
        reason: "no site is selected, so no layer stack can be resolved",
      }
    : undefined;

  // The pack chip is a resolved value too, not a caption.
  const pack = params.find((p) => p.key === "region_pack_code");
  const claimed = new Set<string>(pack ? [pack.key] : []);
  const packWithheld: Withheld = unresolvable ?? {
    state: "NOT_MEASURED",
    template: NOT_AUTHORED,
    reason: "no region pack code is recorded on the site row, so no pack layer took part in this resolve",
  };

  const cards = CARDS.map((card) => {
    const rows = card.rows.map((spec) => {
      const matched = params.filter((p) => !claimed.has(p.key) && spec.match(p.key));
      matched.forEach((p) => claimed.add(p.key));
      return { spec, matched };
    });
    const extra = card.extra
      ? params.filter((p) => !claimed.has(p.key) && card.extra!(p.key))
      : [];
    extra.forEach((p) => claimed.add(p.key));
    return { card, rows, extra };
  });

  // Anything the resolver returned that no §07 card asks for is still listed: the
  // screen never silently drops a value the server resolved.
  const unclaimed = params.filter((p) => !claimed.has(p.key));

  const scopeLine = noSite
    ? "No site selected · configuration resolves per site"
    : props.resolvedAt
      ? `Site ${props.site} · resolved ${props.resolvedAt.slice(0, 10)} from the §5.7 layer stack`
      : `Site ${props.site} · resolved from the §5.7 layer stack (the server stated no resolve date)`;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PageHeader
        title="Configuration"
        family="admin"
        scope={scopeLine}
        showTimeControl={false}
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="chip" title={pack ? pack.reason ?? undefined : packWithheld.reason}>
              {pack ? (
                `${formatValue(pack)}${pack.effective_from ? ` · effective ${pack.effective_from}` : ""}`
              ) : (
                <StateWord state={packWithheld.state} reason={reasonOf(packWithheld)} />
              )}
            </span>
            {props.ruleVersion ? <span className="chip">{props.ruleVersion}</span> : null}
            <button
              type="button"
              className="btn primary"
              aria-expanded={packTestOpen}
              onClick={() => setPackTestOpen((o) => !o)}
            >
              Load second pack (test)
            </button>
          </div>
        }
      />

      {packTestOpen ? (
        <div className="card">
          <h4>
            Load second pack (test)
            <span className="sub">the portability test this control runs</span>
          </h4>
          <p style={noteStyle}>
            No endpoint loads, applies or compares a second pack. The frozen v1 contract
            serves <code>/v1/config-packs</code> read-only and <code>/v1/sites/&#123;id&#125;/effective-config</code>{" "}
            for one site at one moment; neither accepts a pack to try. The control stays —
            a dry-run resolve (a pack code accepted alongside <code>at</code>) would make it
            work, and nothing is simulated in the meantime.
          </p>
        </div>
      ) : null}

      {props.picker}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, alignItems: "start" }}>
        {cards.map(({ card, rows, extra }) => (
          <ConfigCard
            key={card.key}
            title={card.title}
            rows={rows}
            extra={extra}
            footer={card.footer}
            unresolvable={unresolvable}
          />
        ))}
      </div>

      {unclaimed.length > 0 ? (
        <div className="card">
          <h4>
            Other resolved values
            <span className="sub">
              resolved for this site but not asked for by the three cards above
            </span>
          </h4>
          <table className="table" aria-label="Other resolved values">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th style={{ textAlign: "right" }}>From</th>
              </tr>
            </thead>
            <tbody>
              {unclaimed.map((p) => (
                <tr key={p.key}>
                  <td style={{ fontWeight: 600 }}>{humanise(p.key)}</td>
                  <td>{formatValue(p)}</td>
                  <td style={fromCellStyle} title={p.reason ?? undefined}>{formatFrom(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Validation on load */}
      <div className="card">
        <h4>
          Validation on load
          <span className="sub">every pack is validated before it applies: nothing hard-coded anywhere</span>
        </h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          {CHECKS.map((check) => {
            const w = unresolvable ?? check.withheld;
            return (
              <div key={check.label}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600 }}>
                  {/* No outcome was served, so the mark is neither pass nor warn:
                      an unmonitored mark, as on the schematic legend. */}
                  <span aria-hidden="true" style={unknownDotStyle} />
                  {check.label}
                </div>
                <div style={{ fontSize: 11, color: "var(--sk-color-ink-3)", lineHeight: 1.4, marginTop: 2 }}>
                  <StateWord state={w.state} reason={reasonOf(w)} /> — {w.reason}
                </div>
              </div>
            );
          })}
        </div>
        {props.quiet?.text ? (
          <p style={noteStyle}>
            {/* The one statement the server does make about this resolve. */}
            The server states: {props.quiet.text}
            {props.quiet.conditional_on ? ` (conditional on ${props.quiet.conditional_on})` : null}
          </p>
        ) : null}
        {!noSite ? (
          <p style={noteStyle}>
            The resolver returned {params.length} parameter{params.length === 1 ? "" : "s"} for this
            site{props.resolvedAt ? ` as of ${props.resolvedAt.slice(0, 10)}` : ""}. That is a count of
            what was resolved, not a validation result.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ConfigCard(props: {
  title: string;
  rows: { spec: RowSpec; matched: (Param & { key: string })[] }[];
  extra: (Param & { key: string })[];
  footer?: string;
  unresolvable?: Withheld;
}): ReactNode {
  return (
    <div className="card">
      <h4>{props.title}</h4>
      <table className="table" aria-label={props.title}>
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th style={{ textAlign: "right" }}>From</th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map(({ spec, matched }) => {
            if (matched.length === 0) {
              const w = props.unresolvable ?? spec.withheld;
              return (
                <tr key={spec.label}>
                  <td style={{ fontWeight: 600 }}>{spec.label}</td>
                  <td><StateWord state={w.state} reason={reasonOf(w)} /></td>
                  <td style={fromCellStyle}>{w.reason}</td>
                </tr>
              );
            }
            // Several resolved rows can answer one wireframe row (tariff bands,
            // transformer constants): each keeps its own layer and window.
            return matched.map((p) => (
              <tr key={p.key}>
                <td style={{ fontWeight: 600 }}>
                  {matched.length === 1 ? spec.label : `${spec.label} · ${humanise(p.key)}`}
                </td>
                <td>{formatValue(p)}</td>
                <td style={fromCellStyle} title={p.reason ?? undefined}>{formatFrom(p)}</td>
              </tr>
            ));
          })}
          {props.extra.map((p) => (
            <tr key={p.key}>
              <td style={{ fontWeight: 600 }}>{humanise(p.key)}</td>
              <td>{formatValue(p)}</td>
              <td style={fromCellStyle} title={p.reason ?? undefined}>{formatFrom(p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {props.footer ? <p style={noteStyle}>{props.footer}</p> : null}
    </div>
  );
}

const fromCellStyle: React.CSSProperties = {
  textAlign: "right",
  color: "var(--sk-color-ink-3)",
};

const noteStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 11,
  color: "var(--sk-color-ink-3)",
  lineHeight: 1.4,
};

/* Neither pass nor warn: no outcome was served. */
const unknownDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  display: "inline-block",
  flex: "0 0 auto",
  background: "none",
  border: "1.5px dashed var(--sk-color-status-unmonitored)",
};
